import { Box, Paper, Typography, LinearProgress, Button, Grid2 as Grid, alpha, Skeleton, Divider } from "@mui/material";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { HintIcon } from "../ui/mui/HintIcon";

interface SubMetric {
  label: string;
  value: string | number;
  link?: string;
  /** Optional color override for the value text */
  color?: string;
  /** Optional secondary text rendered below the value (e.g., "X under deductible"). */
  caption?: string;
}

interface MetricCardProps {
  icon: ReactNode;
  title: string;
  value: string | number;
  subtitle?: string;
  progress?: number;
  action?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  };
  link?: string;
  accentColor?: string;
  isLoading?: boolean;
  subMetrics?: SubMetric[];
  /** Optional hint text shown via help icon next to title */
  hint?: string;
  /** Optional adornment rendered inline next to the value (e.g., a source chip) */
  valueAdornment?: ReactNode;
  /** Optional color for the progress bar (default: primary.main) */
  progressColor?: string;
  /** Label shown next to the progress bar (default: "Progress") */
  progressLabel?: string;
}

/**
 * Single metric card displaying a key performance indicator or statistic.
 *
 * Features:
 * - Left accent border for visual emphasis
 * - Optional subtitle for context
 * - Optional progress bar for percentage values
 * - Optional action button (e.g., download, view details)
 * - Optional link navigation (makes entire card clickable)
 * - Optional sub-metrics to show breakdown of main value
 * - Optional hint icon next to title for additional context
 * - Loading skeleton state
 *
 * @example
 * ```tsx
 * <MetricCard
 *   icon={<TimeIcon />}
 *   title="Processing Time"
 *   subtitle="Total time"
 *   value="2m 34s"
 *   hint="Average time from claim submission to resolution"
 * />
 * ```
 */
export const MetricCard = ({
  icon,
  title,
  value,
  subtitle,
  progress,
  action,
  link,
  accentColor = "primary.main",
  isLoading = false,
  subMetrics,
  hint,
  progressColor,
  progressLabel,
  valueAdornment,
}: MetricCardProps) => {
  const cardContent = (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        borderLeft: 3,
        borderLeftColor: accentColor,
        borderRadius: 2,
        overflow: "hidden",
        height: "100%",
        transition: "all 0.2s",
        ...(link && {
          cursor: "pointer",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: 1,
            borderLeftColor: "primary.dark",
          },
        }),
      }}
    >
      {/* Compact header + value in one row */}
      <Box sx={{ px: 2, pt: 1.5, pb: subMetrics || progress !== undefined ? 0.5 : 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          {isLoading ? (
            <Skeleton variant="rounded" width={28} height={28} />
          ) : (
            <Box
              sx={{
                p: 0.75,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "& .MuiSvgIcon-root": { fontSize: 18 },
              }}
            >
              {icon}
            </Box>
          )}
          {isLoading ? (
            <Skeleton width={100} height={18} />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {title}
              </Typography>
              {hint && <HintIcon hint={hint} />}
            </Box>
          )}
        </Box>

        {subtitle &&
          (isLoading ? (
            <Skeleton width={60} height={16} sx={{ mb: 0.25 }} />
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 0.25 }}>
              {subtitle}
            </Typography>
          ))}

        {isLoading ? (
          <Skeleton width="50%" height={32} />
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "primary.main",
              }}
            >
              {value}
            </Typography>
            {valueAdornment}
          </Box>
        )}
      </Box>

      {/* Optional Sub-Metrics */}
      {subMetrics && subMetrics.length > 0 && !isLoading && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Divider sx={{ mb: 1 }} />
          <Grid container spacing={1}>
            {subMetrics.map((subMetric, index) => {
              const content = (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.3 }}>
                    {subMetric.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: subMetric.color || "text.primary",
                      ...(subMetric.link && {
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": { color: "primary.main", textDecoration: "underline" },
                      }),
                    }}
                  >
                    {subMetric.value}
                  </Typography>
                  {subMetric.caption && (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ display: "block", lineHeight: 1.3, fontStyle: "italic" }}
                    >
                      {subMetric.caption}
                    </Typography>
                  )}
                </Box>
              );

              return (
                <Grid key={index} size={4}>
                  {subMetric.link ? (
                    <Link to={subMetric.link} style={{ textDecoration: "none", color: "inherit" }}>
                      {content}
                    </Link>
                  ) : (
                    content
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Optional Progress Bar — below sub-metrics, with label */}
      {progress !== undefined && !isLoading && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {progressLabel || "Progress"}
            </Typography>
            <Typography variant="caption" fontWeight={600} sx={{ color: progressColor || "primary.main" }}>
              {Math.round(progress)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: (theme) => alpha(theme.palette.grey[300], 0.3),
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                ...(progressColor && { backgroundColor: progressColor }),
              },
            }}
          />
        </Box>
      )}

      {/* Optional Action Button */}
      {action && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          {isLoading ? (
            <Skeleton variant="rounded" height={32} sx={{ borderRadius: 1 }} />
          ) : (
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={action.onClick}
              startIcon={action.icon}
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.main",
                  color: "primary.contrastText",
                  borderColor: "primary.main",
                },
              }}
            >
              {action.label}
            </Button>
          )}
        </Box>
      )}
    </Paper>
  );

  // Wrap in Link if link prop is provided
  if (link && !isLoading) {
    return (
      <Link to={link} style={{ textDecoration: "none", color: "inherit" }}>
        {cardContent}
      </Link>
    );
  }

  return cardContent;
};

/**
 * Responsive grid container for displaying multiple metric cards.
 */
interface MetricCardsGridProps {
  children: ReactNode;
  /** Number of columns to display on desktop (default: 3) */
  columns?: number;
}

export const MetricCardsGrid = ({ children, columns = 3 }: MetricCardsGridProps) => {
  return (
    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      {Array.isArray(children) ? (
        children.map((child, index) => (
          <Grid key={index} size={{ xs: 12, md: 12 / columns }}>
            {child}
          </Grid>
        ))
      ) : (
        <Grid size={{ xs: 12, md: 12 / columns }}>{children}</Grid>
      )}
    </Grid>
  );
};
